import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Target, Trophy, Flame, ShieldCheck,
  ArrowLeft, Activity, DollarSign, Percent, Clock, RefreshCw, Zap, LineChart,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const neg = n < 0;
  const v = Math.abs(n);
  const str = v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${neg ? "-" : ""}$${str}`;
}
function fmtInt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}
function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(1)}%`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function Kpi({ label, value, sub, tone = "", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4 sm:p-5">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-1 font-display font-extrabold text-2xl sm:text-4xl ${tone || "text-slate-100"}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500">{sub}</div>}
    </div>
  );
}

function SymbolRow({ row, index, tone }) {
  const pos = (row.pnl || 0) >= 0;
  return (
    <div
      data-testid={`perf-symbol-${row.symbol}`}
      className="grid grid-cols-[36px_1fr_auto_auto_auto] gap-3 sm:gap-5 items-center px-3 sm:px-4 py-3 rounded-xl border border-white/5 bg-[#0F111A]/50 hover:border-emerald-400/20 transition"
    >
      <div className={`font-mono font-bold text-center text-sm ${tone === "top" ? "text-amber-300" : "text-rose-300"}`}>
        {tone === "top" ? `#${index + 1}` : `↓${index + 1}`}
      </div>
      <div className="font-display font-bold text-slate-100">{row.symbol}</div>
      <div className="text-right">
        <div className="font-mono text-sm text-slate-200">{fmtInt(row.sells)}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">sells</div>
      </div>
      <div className="text-right">
        <div className={`font-mono text-sm ${row.win_rate >= 70 ? "text-emerald-300" : row.win_rate >= 50 ? "text-amber-300" : "text-rose-300"}`}>
          {fmtPct(row.win_rate)}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">win %</div>
      </div>
      <div className="text-right">
        <div className={`font-display font-bold text-sm ${pos ? "text-emerald-300" : "text-rose-300"}`}>
          {fmtMoney(row.pnl)}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">p&amp;l</div>
      </div>
    </div>
  );
}

function WinRateBar({ wins, losses, label }) {
  const total = (wins || 0) + (losses || 0);
  const wPct = total ? (wins / total) * 100 : 0;
  const lPct = total ? (losses / total) * 100 : 0;
  return (
    <div className="mt-3">
      <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.2em] mb-1.5">
        <span className="text-emerald-300">{wins} wins</span>
        <span className="text-slate-500">{label}</span>
        <span className="text-rose-300">{losses} losses</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-white/5 overflow-hidden flex">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: `${wPct}%` }}
          data-testid="perf-today-wins-bar"
        />
        <div
          className="h-full bg-rose-500 transition-all"
          style={{ width: `${lPct}%` }}
          data-testid="perf-today-losses-bar"
        />
      </div>
    </div>
  );
}

export default function PerformancePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const r = await axios.get(`${API}/api/performance/public`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load performance stats.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  const o = data?.overall;
  const t = data?.today;
  const pf = o?.profit_factor;
  const netPos = (o?.net_pnl || 0) >= 0;

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 relative overflow-x-hidden aq-grain">
      <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-emerald-500/8 via-transparent to-transparent pointer-events-none" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="perf-logo-link">
          <img src="/brand/tradingox-logo.png" alt="TradingoX" className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]" />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <div className="flex items-center gap-3">
          <button
            data-testid="perf-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100 px-3 py-1.5 rounded-full border border-white/10 hover:border-emerald-400/30"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100" data-testid="perf-home-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-16">
        {/* Hero */}
        <section className="text-center pt-4 pb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300" data-testid="perf-hero-badge">
            <Activity className="w-3.5 h-3.5" /> live · public ledger
          </div>
          <h1 className="font-display mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight">
            Every trade. <span className="text-emerald-400">Every outcome.</span> Transparent.
          </h1>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-slate-400 leading-relaxed">
            These are the <b className="text-slate-200">real</b> aggregated results of every automated trade our scalping engine
            has closed — not a backtest, not cherry-picked. Pulled live from our database and updated every 60 seconds.
          </p>
          {data?.period?.first && (
            <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
              Period · {fmtDate(data.period.first)} → {fmtDate(data.period.last)} · generated {new Date(data.generated_at).toLocaleTimeString()}
            </p>
          )}
        </section>

        {err && <div className="mb-6 p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm">{err}</div>}
        {!data && !err && (
          <div className="animate-pulse grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            {[0,1,2,3].map((i) => <div key={i} className="h-28 rounded-2xl bg-white/5" />)}
          </div>
        )}

        {o && (
          <>
            {/* HEADLINE KPIs */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="perf-headline-kpis">
              <Kpi
                testid="perf-kpi-winrate"
                label="Win rate"
                value={fmtPct(o.win_rate_pct)}
                sub={`${fmtInt(o.wins)} W · ${fmtInt(o.losses)} L`}
                tone={o.win_rate_pct >= 70 ? "text-emerald-300" : o.win_rate_pct >= 60 ? "text-amber-300" : "text-rose-300"}
                icon={Target}
              />
              <Kpi
                testid="perf-kpi-pnl"
                label="Net P&L"
                value={fmtMoney(o.net_pnl)}
                sub={netPos ? "after 1.25% fees" : "after 1.25% fees · drawdown"}
                tone={netPos ? "text-emerald-300" : "text-rose-300"}
                icon={DollarSign}
              />
              <Kpi
                testid="perf-kpi-pf"
                label="Profit factor"
                value={pf == null ? "∞" : `${pf.toFixed(2)}×`}
                sub="gross wins ÷ gross losses"
                tone={pf == null || pf >= 1.5 ? "text-emerald-300" : pf >= 1 ? "text-amber-300" : "text-rose-300"}
                icon={Flame}
              />
              <Kpi
                testid="perf-kpi-trades"
                label="Closed trades"
                value={fmtInt(o.sells)}
                sub={`$${fmtInt(Math.round(o.turnover))} turnover`}
                icon={Activity}
              />
            </section>

            {/* HOW IT PROTECTS YOU */}
            <section className="mt-6 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent p-4 sm:p-5" data-testid="perf-guardrails">
              <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                <ShieldCheck className="w-3.5 h-3.5" /> precision mode v4 · how it protects you
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs sm:text-sm">
                <div>
                  <div className="font-display font-bold text-slate-100">1. Picks best-first</div>
                  <div className="text-slate-400 mt-0.5">Ranks every candidate by its learned win rate and buys the strongest first.</div>
                </div>
                <div>
                  <div className="font-display font-bold text-slate-100">2. Rests under 50%</div>
                  <div className="text-slate-400 mt-0.5">If rolling win rate drops below 50% over the last 20 trades, new entries pause while the engine keeps monitoring.</div>
                </div>
                <div>
                  <div className="font-display font-bold text-slate-100">3. Auto-resumes</div>
                  <div className="text-slate-400 mt-0.5">The second the rolling win rate climbs back to 50%+, trading automatically resumes — no manual restart.</div>
                </div>
              </div>
            </section>

            {/* TODAY VS OVERALL */}
            <section className="mt-10 grid lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/8 via-transparent to-transparent p-5 sm:p-6" data-testid="perf-today-panel">
                <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                  <Clock className="w-3.5 h-3.5" /> today · demo + live
                </div>
                <div className="mt-2 flex items-end justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Daily win %</div>
                    <div className="font-display font-extrabold text-4xl sm:text-5xl mt-0.5">
                      <span className={t.win_rate_pct >= 70 ? "text-emerald-300" : t.win_rate_pct >= 50 ? "text-amber-300" : "text-rose-300"}>
                        {fmtPct(t.win_rate_pct)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">P&amp;L today</div>
                    <div className={`font-display font-extrabold text-2xl sm:text-3xl mt-0.5 ${(t.pnl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {fmtMoney(t.pnl)}
                    </div>
                  </div>
                </div>
                <WinRateBar wins={t.wins} losses={t.losses} label={`${fmtInt(t.sells)} sells today`} />
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-5 sm:p-6" data-testid="perf-trade-quality">
                <div className="flex items-center gap-2 text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em]">
                  <ShieldCheck className="w-3.5 h-3.5" /> trade quality
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Avg winner</div>
                    <div className="font-display font-bold text-emerald-300 text-xl mt-0.5">{fmtMoney(o.avg_win)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Avg loser</div>
                    <div className="font-display font-bold text-rose-300 text-xl mt-0.5">{fmtMoney(o.avg_loss)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Best single</div>
                    <div className="font-display font-bold text-emerald-300 text-base mt-0.5">{fmtMoney(o.best)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Worst single</div>
                    <div className="font-display font-bold text-rose-300 text-base mt-0.5">{fmtMoney(o.worst)}</div>
                  </div>
                  <div className="col-span-2 pt-2 border-t border-white/5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Expectancy per trade</div>
                    <div className={`font-display font-bold text-xl mt-0.5 ${(o.expectancy || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {fmtMoney(o.expectancy)}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 30-DAY TREND */}
            {data.trend_30d && data.trend_30d.length > 0 && (() => {
              const dayLabel = (iso) => {
                try {
                  const d = new Date(iso + "T12:00:00Z");
                  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                } catch { return iso; }
              };
              const winPoints = data.trend_30d.map((r) => ({ x: dayLabel(r.date), y: r.win_rate_pct }));
              const pnlPoints = data.trend_30d.map((r) => ({ x: dayLabel(r.date), y: r.pnl }));
              const cumPoints = data.trend_30d.map((r) => ({ x: dayLabel(r.date), y: r.cum_pnl }));
              const activeDays = data.trend_30d.filter((r) => r.sells > 0).length;
              const avgWinRate = (() => {
                const vals = data.trend_30d.filter((r) => r.win_rate_pct != null).map((r) => r.win_rate_pct);
                return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
              })();
              const pnl30 = data.trend_30d.reduce((a, r) => a + (r.pnl || 0), 0);
              return (
                <section className="mt-10" data-testid="perf-trend">
                  <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">30-day trend</div>
                      <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1">Daily performance history</h2>
                      <p className="mt-1 text-xs text-slate-500 max-w-md">
                        {activeDays} active trading days · avg daily win rate <b className="text-slate-300">{avgWinRate.toFixed(1)}%</b> · 30-day P&amp;L <b className={pnl30 >= 0 ? "text-emerald-300" : "text-rose-300"}>{pnl30 >= 0 ? "+" : "-"}${Math.abs(pnl30).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                      <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-emerald-400" /> win %</span>
                      <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-sky-400" /> daily p&amp;l</span>
                      <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-amber-400" /> cumulative</span>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* Win rate sparkline */}
                    <div className="rounded-2xl border border-emerald-400/20 bg-[#0F111A]/70 p-4 sm:p-5" data-testid="perf-trend-winrate-card">
                      <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                        <Target className="w-3.5 h-3.5" /> daily win rate
                      </div>
                      <div className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-slate-100">
                        avg {avgWinRate.toFixed(1)}%
                        <span className="ml-2 text-xs font-mono text-slate-500 font-normal">last 30 days</span>
                      </div>
                      <div className="mt-3">
                        <Sparkline
                          data={winPoints}
                          testid="perf-trend-winrate-chart"
                          stroke="#10B981"
                          referenceY={70}
                          referenceLabel="70% target"
                          yFormat={(v) => `${Math.round(v)}%`}
                          height={160}
                        />
                      </div>
                    </div>

                    {/* Cumulative P&L sparkline (more compelling than daily) */}
                    <div className="rounded-2xl border border-amber-400/20 bg-[#0F111A]/70 p-4 sm:p-5" data-testid="perf-trend-cumpnl-card">
                      <div className="flex items-center gap-2 text-amber-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                        <LineChart className="w-3.5 h-3.5" /> cumulative net p&amp;l
                      </div>
                      <div className={`mt-1 font-display text-xl sm:text-2xl font-extrabold ${pnl30 >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                        {pnl30 >= 0 ? "+" : "-"}${Math.abs(pnl30).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        <span className="ml-2 text-xs font-mono text-slate-500 font-normal">over 30 days</span>
                      </div>
                      <div className="mt-3">
                        <Sparkline
                          data={cumPoints}
                          testid="perf-trend-cumpnl-chart"
                          stroke="#F59E0B"
                          referenceY={0}
                          referenceLabel="break-even"
                          yFormat={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)}
                          height={160}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Daily P&L bar underneath for extra signal */}
                  <div className="mt-4 rounded-2xl border border-sky-400/20 bg-[#0F111A]/70 p-4 sm:p-5" data-testid="perf-trend-dailypnl-card">
                    <div className="flex items-center gap-2 text-sky-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                      <Activity className="w-3.5 h-3.5" /> daily p&amp;l (realized)
                    </div>
                    <div className="mt-3">
                      <Sparkline
                        data={pnlPoints}
                        testid="perf-trend-dailypnl-chart"
                        stroke="#38BDF8"
                        referenceY={0}
                        referenceLabel="break-even"
                        yFormat={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)}
                        height={140}
                      />
                    </div>
                  </div>
                </section>
              );
            })()}

            {/* TOP SYMBOLS */}
            {data.top_symbols && data.top_symbols.length > 0 && (
              <section className="mt-10" data-testid="perf-top-symbols">
                <div className="flex items-end justify-between gap-3 mb-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">heroes</div>
                    <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1">Best performing symbols</h2>
                  </div>
                  <div className="hidden sm:block text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                    sells · win % · p&amp;l
                  </div>
                </div>
                <div className="space-y-2">
                  {data.top_symbols.map((row, i) => <SymbolRow key={row.symbol} row={row} index={i} tone="top" />)}
                </div>
              </section>
            )}

            {/* BOTTOM SYMBOLS */}
            {data.bottom_symbols && data.bottom_symbols.length > 0 && (
              <section className="mt-10" data-testid="perf-bottom-symbols">
                <div className="flex items-end justify-between gap-3 mb-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-rose-300">on probation</div>
                    <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1">Underperforming symbols</h2>
                    <p className="mt-1 text-xs text-slate-500">Precision Mode v3 auto-blocks any symbol under 70% historical win rate once we have 10+ samples.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {data.bottom_symbols.map((row, i) => <SymbolRow key={row.symbol} row={row} index={i} tone="bottom" />)}
                </div>
              </section>
            )}

            {/* FOOTER */}
            <section className="mt-14 rounded-2xl border border-white/10 bg-[#0F111A]/60 p-5 sm:p-6" data-testid="perf-disclaimer">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">disclaimer</div>
              <p className="mt-1 text-xs sm:text-sm text-slate-400 leading-relaxed">
                Numbers on this page are aggregated results across every account trading through TradingoX. They include paper trading accounts and may include live brokerage activity when enabled by individual members.
                Past performance does not guarantee future results. All trades carry risk of loss. Review the full{" "}
                <Link to="/signup" className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200">Risk Disclosure</Link>{" "}
                before signing up.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
